import React, { useState } from 'react';
import './AuthPage.css';
import { supabase, isSupabaseConfigured } from '../services/supabase';

interface AuthPageProps {
  onSuccess: (userData: any) => void;
}

type AuthMode = 'signin' | 'signup' | 'forgot' | 'mfa' | 'reset';

const AuthPage: React.FC<AuthPageProps> = ({ onSuccess }) => {
  const [mode, setMode] = useState<AuthMode>('signin');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [username, setUsername] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [mfaFactorId, setMfaFactorId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleSignIn = async () => {
    setError('');
    setIsLoading(true);
    try {
      if (!supabase) throw new Error('Supabase not configured');
      
      const { data, error: authError } = await supabase.auth.signInWithPassword({ email, password });
      
      if (authError) {
        if (authError.message.includes('Invalid login credentials')) {
          setError('Invalid email or password');
        } else {
          setError(authError.message);
        }
        setIsLoading(false);
        return;
      }

      // Check if MFA is required
      if (data?.user) {
        const { data: factors } = await supabase.auth.mfa.listFactors();
        const totpFactor = factors?.all?.find(f => f.status === 'verified' && f.factor_type === 'totp');
        
        if (totpFactor) {
          // MFA required — show code input
          setMfaFactorId(totpFactor.id);
          setMode('mfa');
          setIsLoading(false);
          return;
        }

        // No MFA — proceed
        await completeLogin(data.user);
      }
    } catch (err: any) {
      setError(err.message || 'Login failed');
    }
    setIsLoading(false);
  };

  const handleMfaVerify = async () => {
    setError('');
    setIsLoading(true);
    try {
      if (!supabase || !mfaFactorId) throw new Error('MFA not initialized');

      const { data: challenge, error: challengeError } = await supabase.auth.mfa.challengeAndVerify({
        factorId: mfaFactorId,
        code: mfaCode,
      });

      if (challengeError) {
        setError('Invalid verification code. Try again.');
        setIsLoading(false);
        return;
      }

      // MFA verified — get the full user
      const { data: { user } } = await supabase.auth.getUser();
      if (user) await completeLogin(user);
    } catch (err: any) {
      setError(err.message || 'Verification failed');
    }
    setIsLoading(false);
  };

  const handleSignUp = async () => {
    if (password !== confirmPassword) { setError('Passwords do not match'); return; }
    if (password.length < 8) { setError('Password must be at least 8 characters'); return; }
    if (!username || username.length < 2) { setError('Username must be at least 2 characters'); return; }
    setError('');
    setIsLoading(true);
    try {
      if (!supabase) throw new Error('Supabase not configured');

      const { data, error: authError } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: { username },
        },
      });

      if (authError) {
        if (authError.message.includes('already registered')) {
          setError('An account with this email already exists');
        } else {
          setError(authError.message);
        }
        setIsLoading(false);
        return;
      }

      if (data?.user) {
        // Create user record in our users table
        const userId = data.user.id;
        await supabase.from('users').upsert({
          id: userId,
          email: email,
          username: username,
          balls_balance: 0,
        });

        if (data.session) {
          // Auto-confirmed (no email verification required)
          await completeLogin(data.user);
        } else {
          // Email confirmation required
          setSuccess('Check your email for a confirmation link, then sign in.');
          setMode('signin');
        }
      }
    } catch (err: any) {
      setError(err.message || 'Registration failed');
    }
    setIsLoading(false);
  };

  // Detect password recovery session on mount
  React.useEffect(() => {
    const checkRecovery = async () => {
      if (!supabase) return;
      
      // Check URL query params (set by our PASSWORD_RECOVERY handler in App.tsx)
      const params = new URLSearchParams(window.location.search);
      if (params.get('mode') === 'reset') {
        setMode('reset');
        return;
      }
      
      // Also check the URL hash (Supabase includes type=recovery in the hash)
      const hash = window.location.hash;
      if (hash.includes('type=recovery')) {
        setMode('reset');
        return;
      }
      
      // Also check if there's an active session from a recovery link
      // (hash may have been consumed already but session exists)
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        // Check the user's app_metadata for recovery indicator
        // Supabase doesn't expose this directly, so we rely on URL params above
      }
    };
    checkRecovery();
  }, []);

  const handleResetPassword = async () => {
    setError('');
    setIsLoading(true);
    try {
      if (!supabase) throw new Error('Supabase not configured');
      if (newPassword !== confirmNewPassword) { setError('Passwords do not match'); setIsLoading(false); return; }
      if (newPassword.length < 8) { setError('Password must be at least 8 characters'); setIsLoading(false); return; }

      const { error: updateError } = await supabase.auth.updateUser({ password: newPassword });
      if (updateError) throw updateError;

      setSuccess('Password updated! Signing you in...');
      // Now complete login with the updated session
      const { data: { user } } = await supabase.auth.getUser();
      if (user) await completeLogin(user);
    } catch (err: any) {
      setError(err.message || 'Password reset failed');
    }
    setIsLoading(false);
  };

  const handleForgotPassword = async () => {
    setError('');
    setIsLoading(true);
    try {
      if (!supabase) throw new Error('Supabase not configured');
      const { error: resetError } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: window.location.origin,
      });
      if (resetError) throw resetError;
      setSuccess('Password reset link sent to your email.');
      setMode('signin');
    } catch (err: any) {
      setError(err.message || 'Reset failed');
    }
    setIsLoading(false);
  };

  async function completeLogin(authUser: any) {
    // Get or create user record in our users table
    // First try matching by auth id (normal flow)
    let { data: existingUser } = await supabase.from('users').select('*').eq('id', authUser.id).single();
    
    // If not found, try matching by email (migration from pre-auth system)
    if (!existingUser && authUser.email) {
      const { data: emailUser } = await supabase.from('users').select('*').eq('email', authUser.email).single();
      if (emailUser) {
        // Found existing user by email — update their id to match auth id
        const { data: updated } = await supabase.from('users')
          .update({ id: authUser.id })
          .eq('id', emailUser.id)
          .select().single();
        existingUser = updated || { ...emailUser, id: authUser.id };
      }
    }
    
    let userData: any;
    if (existingUser) {
      userData = existingUser;
    } else {
      // First time — create user record
      const meta = authUser.user_metadata || {};
      const { data: newUser } = await supabase.from('users').insert({
        id: authUser.id,
        email: authUser.email,
        username: meta.username || authUser.email?.split('@')[0] || 'Player',
        balls_balance: 0,
      }).select().single();
      userData = newUser;
    }

    localStorage.setItem('userId', authUser.id);
    localStorage.setItem('userData', JSON.stringify(userData));
    onSuccess(userData);
  }

  return (
    <div className="auth-page">
      <div className="auth-container">
        <div className="auth-branding">
          <h1><span className="gradient-text">SimForge</span> Baseball</h1>
          <p>{mode === 'signin' ? 'Sign in to continue' : mode === 'signup' ? 'Create your account' : mode === 'forgot' ? 'Reset your password' : 'Two-Factor Verification'}</p>
        </div>

        {error && <div className="error-message">{error}</div>}
        {success && <div className="success-message">{success}</div>}

        {mode === 'mfa' ? (
          <div className="auth-form">
            <div className="mfa-icon">🔐</div>
            <p style={{ color: '#888', textAlign: 'center', marginBottom: 20, fontSize: '0.9rem' }}>
              Enter the code from your authenticator app
            </p>
            <div className="form-group">
              <label>Verification Code</label>
              <input type="text" value={mfaCode} onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, '').slice(6))} placeholder="000000" autoComplete="one-time-code" maxLength={6}
                style={{ textAlign: 'center', fontSize: '1.5rem', letterSpacing: '0.5rem' }} />
            </div>
            <button className="primary-btn" onClick={handleMfaVerify} disabled={isLoading || mfaCode.length !== 6}>
              {isLoading ? 'Verifying...' : 'Verify'}
            </button>
          </div>
        ) : mode === 'reset' ? (
          <div className="auth-form">
            <div className="mfa-icon">🔑</div>
            <p style={{ color: '#ccc', textAlign: 'center', marginBottom: 20, fontSize: '0.95rem' }}>
              Set your new password
            </p>
            <div className="form-group">
              <label>New Password</label>
              <input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="••••••••" autoComplete="new-password" />
            </div>
            <div className="form-group">
              <label>Confirm New Password</label>
              <input type="password" value={confirmNewPassword} onChange={(e) => setConfirmNewPassword(e.target.value)} placeholder="••••••••" autoComplete="new-password" onKeyDown={(e) => e.key === 'Enter' && handleResetPassword()} />
            </div>
            <button className="primary-btn" onClick={handleResetPassword} disabled={isLoading || !newPassword || !confirmNewPassword}>
              {isLoading ? 'Updating...' : 'Reset Password'}
            </button>
          </div>
        ) : mode === 'forgot' ? (
          <div className="auth-form">
            <div className="form-group">
              <label>Email</label>
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="your@email.com" autoComplete="email" />
            </div>
            <button className="primary-btn" onClick={handleForgotPassword} disabled={isLoading || !email}>
              {isLoading ? 'Sending...' : 'Send Reset Link'}
            </button>
            <div className="auth-footer">
              <button className="link-btn" onClick={() => setMode('signin')}>← Back to Sign In</button>
            </div>
          </div>
        ) : mode === 'signin' ? (
          <div className="auth-form">
            <div className="form-group">
              <label>Email</label>
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="your@email.com" autoComplete="email" />
            </div>
            <div className="form-group">
              <label>Password</label>
              <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" autoComplete="current-password" onKeyDown={(e) => e.key === 'Enter' && handleSignIn()} />
            </div>
            <button className="primary-btn" onClick={handleSignIn} disabled={isLoading || !email || !password}>
              {isLoading ? 'Signing In...' : 'Sign In'}
            </button>
            <div className="auth-footer">
              <span>Don't have an account?</span>
              <button className="link-btn" onClick={() => { setMode('signup'); setError(''); setSuccess(''); }}>Sign Up</button>
            </div>
            <button className="link-btn" onClick={() => { setMode('forgot'); setError(''); setSuccess(''); }} style={{ marginTop: 8 }}>Forgot password?</button>
          </div>
        ) : (
          <div className="auth-form">
            <div className="form-group">
              <label>Username</label>
              <input type="text" value={username} onChange={(e) => setUsername(e.target.value)} placeholder="yourname" autoComplete="username" />
            </div>
            <div className="form-group">
              <label>Email</label>
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="your@email.com" autoComplete="email" />
            </div>
            <div className="form-group">
              <label>Password</label>
              <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" autoComplete="new-password" />
            </div>
            <div className="form-group">
              <label>Confirm Password</label>
              <input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} placeholder="••••••••" autoComplete="new-password" onKeyDown={(e) => e.key === 'Enter' && handleSignUp()} />
            </div>
            <button className="primary-btn" onClick={handleSignUp} disabled={isLoading || !email || !password || !username}>
              {isLoading ? 'Creating Account...' : 'Create Account'}
            </button>
            <div className="auth-footer">
              <span>Already have an account?</span>
              <button className="link-btn" onClick={() => { setMode('signin'); setError(''); setSuccess(''); }}>Sign In</button>
            </div>
          </div>
        )}

        <div className="auth-note">
          <span className="note-icon">🔐</span>
          <span>Protected by Supabase Auth{mode === 'mfa' ? ' + 2FA' : ''}</span>
        </div>
      </div>
    </div>
  );
};

export default AuthPage;
